import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AreaChart,
  AreaSeries,
  ChartTooltip,
  Gridline,
  GridlineSeries,
  Line,
  LinearXAxis,
  LinearXAxisTickLabel,
  LinearXAxisTickSeries,
  LinearYAxis,
  LinearYAxisTickLabel,
  LinearYAxisTickSeries,
  schemes,
  TooltipArea,
} from 'reaviz';
import { CHART_COLORS } from '../../../../modules/reports/constants/chart-colors';
import { CustomTooltipContentAttendanceChart } from '../../../../tools/components/CustomTooltipContentAttendanceChart';
import { AttendancePeriodType, AverageSeriesData } from '../../hooks/useAttendanceData';

interface AverageAttendanceChartProps {
  data: AverageSeriesData[];
  formatXAxisDate: (value: any) => string;
  selectedPeriod?: AttendancePeriodType;
}

export const AverageAttendanceChart: React.FC<AverageAttendanceChartProps> = ({
  data,
  formatXAxisDate,
  selectedPeriod = 'current_month',
}) => {
  const { t } = useTranslation('dashboard');
  // Use fixed color from constants for average values
  const averageColor = CHART_COLORS.attendance.average;

  // Calculate maximum hours value from data for Y axis configuration
  const maxHoursValue = useMemo(() => {
    if (!data || data.length === 0) return 10; // Default 10 hours

    let maxValue = 0;
    data.forEach((point) => {
      if (point.value > maxValue) {
        maxValue = point.value;
      }
    });

    // Round up maximum value to nearest integer and add margin
    const roundedMax = Math.ceil(maxValue);
    return Math.max(10, roundedMax + 2); // Minimum 10 hours, or maximum value + 2 hours margin
  }, [data]);

  // Determine period type for tooltip formatting
  const getPeriodType = (): 'day' | 'week' | 'month' | 'year' => {
    switch (selectedPeriod) {
      case 'current_month':
        return 'day';
      case 'last_3_months':
        return 'week';
      case 'last_6_months':
        return 'month';
      default:
        return 'day';
    }
  };

  const periodType = getPeriodType();

  return (
    <AreaChart
      data={data}
      gridlines={<GridlineSeries line={<Gridline strokeWidth={0.5} strokeDasharray="4 4" />} />}
      series={
        <AreaSeries
          colorScheme={[averageColor]} // Use array with single color
          interpolation="linear"
          line={<Line strokeWidth={2} />}
          tooltip={
            <TooltipArea
              tooltip={
                <ChartTooltip
                  followCursor={true}
                  content={(tooltipData: any, color: string) => {
                    // For average values chart
                    const date = tooltipData.datum?.key || new Date(tooltipData.x);
                    const value =
                      typeof tooltipData.datum?.data === 'number'
                        ? tooltipData.datum.data
                        : typeof tooltipData.y === 'number'
                          ? tooltipData.y
                          : 0;

                    // Use color from constants for average values
                    const itemColor = averageColor;

                    const finalTooltipData = {
                      x: date instanceof Date ? date.getTime() : date,
                      y: value,
                      data: [
                        {
                          key: t('attendance.average'),
                          y: value,
                          color: itemColor, // Use color from constants
                        },
                      ],
                      periodType, // Add periodType to tooltip data
                    };

                    return (
                      <CustomTooltipContentAttendanceChart
                        data={finalTooltipData}
                        color={itemColor}
                        periodType={periodType} // Pass period type to tooltip component
                      />
                    );
                  }}
                />
              }
            />
          }
        />
      }
      xAxis={
        <LinearXAxis
          type="time"
          tickSeries={
            <LinearXAxisTickSeries
              label={<LinearXAxisTickLabel format={formatXAxisDate} fontSize={11} padding={5} />}
            />
          }
        />
      }
      yAxis={
        <LinearYAxis
          domain={[0, maxHoursValue]}
          tickSeries={
            <LinearYAxisTickSeries
              label={
                <LinearYAxisTickLabel
                  format={(value: number) => t('attendance.hoursAxisLabel', { value })}
                  fontSize={11}
                  padding={5}
                />
              }
            />
          }
        />
      }
    />
  );
};
